import { Operations } from "../../MathFactsGenerator/Constants.ts";

/**
 * The best score this browser has reached on a quiz, kept per operation and
 * length so "5 in 30 seconds" and "5 untimed" do not compete.
 *
 * Local to the browser, like Shared/RecentWorksheets.ts: every access guarded,
 * nothing sent anywhere, and worth keeping precisely because it asks for
 * nothing.
 */

const STORAGE_KEY = "mathpages.quizBestScores";

/** A stable key for one kind of run. `null` duration means untimed. */
export function bestScoreKey(operation: Operations, durationMs: number | null): string {
    return `${operation}:${durationMs ?? "untimed"}`;
}

/**
 * Math Facts Soccer keys by the operations played and the keeper (ADR 0059
 * §6): the page lets a child pick or mix operations, so the route's operation
 * is not what was played, and a Rookie score is no Legend record.
 */
export function soccerBestScoreKey(operations: string[], keeper: string): string {
    return `${[...operations].sort().join("+")}:soccer-${keeper}`;
}

/**
 * Math Facts Football keys by the operations played and the defense (ADR 0103
 * §4), as Soccer does by keeper: an All-Pro score is no Rookie record.
 */
export function footballBestScoreKey(operations: string[], defense: string): string {
    return `${[...operations].sort().join("+")}:football-${defense}`;
}

/**
 * Math Runner keys by the operations played and the pace (ADR 0124 §3): a
 * Sprint score is no Jog record, and the speed follows the child from a
 * different starting time at each.
 */
export function runnerBestScoreKey(operations: string[], pace: string): string {
    return `${[...operations].sort().join("+")}:runner-${pace}`;
}

/**
 * Factor Merge keeps a best for each grade (ADR 0128 §4, §7): there are no
 * operations to choose and no pace, so the grade ("g3" to "g8") is the whole key. It has the
 * same shape as the others - `<what was played>:<game>` - so one blob holds all.
 */
export function mergeBestScoreKey(level: string): string {
    return `${level}:merge`;
}

/**
 * Shop Rush keeps a best for each grade and pace (ADR 0131 §1, §7): a Calm shift is
 * no Rush record, and the grades are different orders altogether. Same shape
 * as the others - `<what was played>:<game>` - so one blob holds all.
 */
export function shopBestScoreKey(grade: string, pace: string): string {
    return `${grade}:shop-${pace}`;
}

/**
 * Hidden Picture keeps a best for each set of operations (ADR 0133 §1), as the
 * other picker games do: a score on addition facts is no record on division. There
 * is no pace or level, so the operations are the whole of what was played. Same
 * shape as the others - `<what was played>:<game>` - so one blob holds all.
 */
export function pictureBestScoreKey(operations: string[]): string {
    return `${[...operations].sort().join("+")}:picture`;
}

/**
 * The Baker Street Math Crossword keeps a best casebook for each rank (ADR 0134 §5): a
 * Constable's 13 is no Detective's. Same shape as the others, `<what was played>:<game>`.
 */
export function crosswordBestScoreKey(rank: string): string {
    return `${rank}:crossword`;
}

/**
 * Math Munch keeps calm and chased scores apart (ADR 0056 §5), and one set of
 * operations from another (ADR 0098 §4). Multiplication alone is still
 * `Multiplication:munch`, the key it had before the choice, so saved scores survive.
 */
export function munchBestScoreKey(operations: string[], calm: boolean): string {
    return `${[...operations].sort().join("+")}:munch${calm ? "-calm" : ""}`;
}

function readAll(): Record<string, number> {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return {};
        }
        const parsed = JSON.parse(stored);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }
        const clean: Record<string, number> = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (typeof value === "number" && Number.isFinite(value)) {
                clean[key] = value;
            }
        }
        return clean;
    } catch {
        return {};
    }
}

export function readBestScore(key: string): number | null {
    const all = readAll();
    return key in all ? all[key] : null;
}

/**
 * Records a finished run's score and returns the best for that key afterwards,
 * so the caller can tell whether it was a new best.
 */
export function recordScore(key: string, score: number): number {
    const all = readAll();
    const previous = all[key] ?? 0;
    const best = Math.max(previous, score);
    if (best !== previous) {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [key]: best }));
        } catch {
            // Full, disabled, or unavailable. Not worth an error over a high score.
        }
    }
    return best;
}
